import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useUpdateProcedureNotes } from 'hooks/useProcedure';
import { snackbarService } from 'services/snackbarService';
import { useAuth } from 'context/AuthContext';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import CustomText from 'components/common/CustomText/CustomText';
import NoteItem from '../NoteItem/NoteItem';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import AddIcon from 'icons/AddIcon';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import { hexWithOpacity } from 'common/utils';
import CustomDialog from 'components/dialogs/CustomDialog';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { EPermission } from 'enum/EPermission';

interface Props {
  notes: any[];
  procedureId: number;
  refetch: () => void;
  isEditor: boolean;
}

export default function CaseNotesDetails({
  notes = [],
  procedureId,
  refetch,
  isEditor,
}: Props) {
  const { user, doctor } = useAuth();
  const globalStyles = useGlobalStyles();

  const [isEditMode, setIsEditMode] = useState(false);
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const methods = useForm({
    defaultValues: {
      notes,
    },
  });

  const {
    control,
    handleSubmit,
    register,
    reset,
    getValues,
    formState: { errors },
  } = methods;

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    reset({ notes });
  }, [notes, reset]);

  const { mutate: updateMutation, isLoading } =
    useUpdateProcedureNotes(
      () => {
        snackbarService.showSnackbar('Notes have been updated successfully', 'success');
        setIsEditMode(false);
        refetch();
      },
      (error: any) => {
        console.error(error);
      }
    );

  const onSubmit = (values: any) => {
    updateMutation({
      userId: user.id,
      procedureId,
      notes: values.notes,
    });
  };

  const renderNote = (note: any, index: number) => (
    <Grid size={{ xs: 12, md: 6 }} key={note.id}>
      <NoteItem item={note} index={index} />
    </Grid>
  );

  return (
    <FormProvider {...methods}>
      <Box className={globalStyles.sectionContainer}>
        {/* Header */}
        <Box className={globalStyles.sectionHeader}>
          <CustomText
            text="Notes"
            color={colors.primary}
            fontWeight={600}
          />

          {isEditor && (
            <Box mr={2} display='flex' gap={1}>
              {isEditMode ? (
                <>
                  <Tooltip title="Save">
                    <IconButton
                      onClick={handleSubmit(onSubmit)}
                      className={globalStyles.sectionButton}
                      sx={{
                        backgroundColor: `${hexWithOpacity(colors.success, 0.1)} !important`,
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <FileDownloadDoneIcon color="success" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Add">
                    <IconButton
                      onClick={() => append({ note: '' })}
                      className={globalStyles.sectionButton}
                    >
                      <AddIcon size={22} color={colors.primary} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <IconButton
                  onClick={() => setIsEditMode(true)}
                  className={globalStyles.sectionButton}
                >
                  <DriveFileRenameOutlineIcon
                    sx={{ fontSize: 22, color: colors.primary }}
                  />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        <Divider />


        {/* View Mode */}
        {!isEditMode && fields.length > 0 && (
          <Grid container spacing={1.5} sx={{ px: 2.5, my: 2 }}>
            {notes.map((note, index) => renderNote(note, index))}
          </Grid>
        )}


        {/* Edit Mode */}
        {isEditMode && fields.length > 0 && (
          <Grid container spacing={1.5} pr={2.5} pl={2.5} my={2}>
            {fields.map((field, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Box flex={1}>
                    <CustomFormTextInput
                      name={`notes.${index}.note`}
                      placeholder={`Note ${index + 1}`}
                      register={register}
                      error={(errors as any)?.notes?.[index]}
                      multiline
                      minRows={3}
                    />
                  </Box>
                  <IconButton
                    onClick={() => setRemoveIndex(index)}
                    color="error"
                    sx={{ padding: 0 }}
                  >
                    <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Stack>


                {/* Remove Dialog */}
                <CustomDialog
                  open={removeIndex === index}
                  title={'Remove Note'}
                  message={'Are you sure you want to remove this note?'}
                  buttons={[
                    <CustomButton
                      variant='outlined'
                      text='Cancel'
                      textColor='error'
                      onClick={() => setRemoveIndex(null)}
                      height={35}
                    />,
                    <CustomButton
                      variant='filled'
                      color='error'
                      text='Remove'
                      onClick={() => {
                        remove(index);
                        setRemoveIndex(null);
                      }}
                      height={35}
                    />,
                  ]}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </FormProvider>
  );
}
